import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { startCase } from "lodash";
import {
  Box,
  Typography,
  Button,
  TextField,
  InputAdornment,
  Alert,
  Card,
  Stack,
  Pagination,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Switch,
  FormControlLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AttachFile as AttachFileIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import NewBlockDialog from "./NewBlockDialog";

const splitBlockByGap = (fields) => {
  if (!fields || fields.length === 0) return [];

  // 1. Sort fields top-to-bottom by Y position
  const sorted = [...fields].sort((a, b) => a.y - b.y);

  const subBlocks = [];
  let currentGroup = [sorted[0]];

  for (let i = 1; i < sorted.length; i++) {
    const prevField = sorted[i - 1];
    const currField = sorted[i];

    // Gap = top of current field minus bottom of previous field
    const gap = currField.y - (prevField.y + prevField.height);

    if (gap > 15) {
      // Large gap found → close current group, start new one
      subBlocks.push(currentGroup);
      currentGroup = [currField];
    } else {
      currentGroup.push(currField);
    }
  }

  // Push the last group
  if (currentGroup.length > 0) subBlocks.push(currentGroup);

  return subBlocks;
};

const FormBlockBrowser = ({
  currentProcessingFile,
  setCurrentProcessingFile,
  onNotify,
  loading: loadingDoc,
}) => {
  const currentSession = useSelector((state) => state.sessions.currentSession);

  const [openNewBlockDialog, setOpenNewBlockDialog] = useState(false);
  const [openDeleteBlockDialog, setOpenDeleteBlockDialog] = useState(false);
  const [showFields, setShowFields] = useState(false);

  const [selectedBlock, setSelectedBlock] = useState(null);
  const [searchBlock, setSearchBlock] = useState("");
  const [searchField, setSearchField] = useState("");
  const [blocks, setBlocks] = useState([]);
  const [fields, setFields] = useState([]);
  const [filteredBlock, setFilteredBlock] = useState(blocks);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const [totalPage, setTotalPage] = useState(0);

  const debounceTimerRef = useRef(null);

  useEffect(() => {
    if (error) {
      const interv = setTimeout(() => {
        setError(false);
      }, 5000);
      return () => clearTimeout(interv);
    }
  }, [error]);

  const getBlocks = useCallback(
    async (searchQuery, page) => {
      setLoading(true);
      setError("");

      try {
        const res = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/form-blocks/list`,
          { currentSession, page, limit: 5, query: searchQuery },
          { withCredentials: true },
        );

        if (res.data.success) {
          const blocks = res.data.data?.blocks;
          setBlocks(blocks);
          setFilteredBlock(blocks);
          setTotalPage(res.data.data?.total_pages);
        }
      } catch (error) {
        setError(
          error?.response?.data?.message ||
            "An error occurred while fetching sections",
        );
      } finally {
        setLoading(false);
      }
    },
    [currentSession],
  );

  const getFormFields = useCallback(async () => {
    setError("");

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/list`,
        { currentSession },
        { withCredentials: true },
      );

      if (res.data.success) {
        const fields = res.data.data?.fields;
        setFields(fields);
      }
    } catch (error) {
      setError(
        error?.response?.data?.message ||
          "An error occurred while fetching fields",
      );
    }
  }, [currentSession]);

  useEffect(() => {
    getBlocks("", 1);
    getFormFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCloseNewBlockDialog = () => {
    setOpenNewBlockDialog(false);
    setSelectedBlock(null);
  };

  const handleCloseDeleteBlockDialog = () => {
    setOpenDeleteBlockDialog(false);
    setSelectedBlock(null);
  };

  const handlePageChange = (e, value) => {
    setPage(value);
    getBlocks(searchBlock, value);
  };

  const handleChange = (e, setter, isFieldSearch) => {
    const val = e.target.value;
    setter(val);

    if (!isFieldSearch) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      debounceTimerRef.current = setTimeout(() => {
        getBlocks(val, 1);
        setPage(1);
      }, 1000);
    }
  };

  const handleDeleteConfirm = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/form-blocks/delete`,
        {
          block_id: selectedBlock?.block_id,
          currentSession: currentSession,
        },
        {
          withCredentials: true,
        },
      );

      if (response?.data?.success) {
        getBlocks(searchBlock, page);
        handleCloseDeleteBlockDialog();
      }
    } catch (error) {
      setError(
        error?.response?.data?.message ||
          `An error occurred while deleting section`,
      );
    } finally {
      setLoading(false);
    }
  };

  const pickRectComponent = (rect = {}, upper, lower, fallback = 0) => {
    const value = rect?.[upper] ?? rect?.[lower] ?? fallback;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : Number(fallback) || 0;
  };

  const getBoundingBox = useCallback(
    (fields, padding = 8) => {
      const currentDocument =
        currentProcessingFile?.document?.tempFields?.currentDocument || {};

      const minY = Math.min(...fields.map((f) => f.y));

      const topField = fields?.length
        ? fields.reduce((maxItem, item) =>
            item.y < maxItem.y ? item : maxItem,
          )
        : {};

      const y = pickRectComponent(topField, "Y", "y", 0);

      const maxY = Math.max(...fields.map((f) => f.y + f.height));
      return {
        x: 20,
        y: y - 3,
        width: currentDocument?.actualWidth - 50,
        height: maxY - minY + padding * 0.5,
      };
    },
    [currentProcessingFile?.document?.tempFields?.currentDocument],
  );

  const createBlocks = (formFields) => {
    const rawGroups = formFields.reduce((acc, field) => {
      if (!field.block_id) return acc;
      if (!acc[field.block_id]) acc[field.block_id] = [];
      acc[field.block_id].push(field);
      return acc;
    }, {});

    // Step 2: for each block_id, split by Y gap
    const splitGroups = {};

    Object.entries(rawGroups).forEach(([blockId, fields]) => {
      const subBlocks = splitBlockByGap(fields);

      if (subBlocks.length === 1) {
        // No split needed - keep original block_id
        splitGroups[blockId] = subBlocks[0];
      } else {
        // Multiple sub-blocks - suffix with index
        subBlocks.forEach((subFields, idx) => {
          splitGroups[`${blockId}__sub_${idx}`] = subFields;
        });
      }
    });

    return Object.fromEntries(
      Object.entries(splitGroups).map(([blockId, groupFields]) => {
        const bb = getBoundingBox(groupFields, 8);
        return [
          blockId,
          {
            ...bb,
            blockLabel: groupFields?.[0]?.block_label,
            page: groupFields?.[0]?.page,
            fields: groupFields,
          },
        ];
      }),
    );
  };

  const handleAttach = (block) => {
    const tempFields = currentProcessingFile?.document?.tempFields;
    const selectedFieldIds = tempFields?.selectedExtraFields?.map(
      (f) => f?.name,
    );

    const updatedSelectedFields = tempFields?.selectedExtraFields?.map((f) => ({
      ...f,
      block_id: block?.block_id,
      block_label: block?.block_label,
    }));

    const blocks = createBlocks(updatedSelectedFields);

    const updatedBlock = Object?.entries(blocks)?.map(([id, detail]) => {
      const uniqueName = `block__${Date.now().toString().slice(-4)}`;
      return {
        block_id: id,
        block_label: detail?.blockLabel,
        name: uniqueName,
        type: "PDFRectangle",
        value: "",
        x: detail?.x, // Convert X to points
        y: detail?.y, // Convert Y to points and flip
        width: detail?.width, // Convert width to points
        height: detail?.height, // Convert height to points
        page: tempFields?.currentDocument?.pageNumber,
        fontSize: detail?.height, // Font size based on height
        fontSizes: [],
        autoFontSize: true,
      };
    });

    const fieldToSplitBlockId = {};
    Object.entries(blocks).forEach(([splitId, detail]) => {
      detail.fields.forEach((f) => {
        fieldToSplitBlockId[f.name] = {
          block_id: splitId,
          block_label: detail.blockLabel,
        };
      });
    });

    const updatedFormFields = tempFields?.formFields?.map((f) => {
      if (selectedFieldIds?.includes(f?.name) && fieldToSplitBlockId[f.name]) {
        return {
          ...f,
          block_id: fieldToSplitBlockId[f.name].block_id,
          block_label: fieldToSplitBlockId[f.name].block_label,
        };
      }
      return f;
    });

    setCurrentProcessingFile((prevFile) => ({
      ...prevFile,
      document: {
        ...prevFile.document,
        tempFields: {
          ...prevFile.document.tempFields,
          formFields: [...updatedFormFields, ...updatedBlock],
          selectedExtraFields: updatedSelectedFields,
        },
      },
    }));
  };

  const handleShowFields = (event) => {
    setShowFields(event.target.checked);
  };

  const fieldsByBlock = useMemo(() => {
    const lowerSearch = searchField?.toLowerCase();

    // Group fields by parent: separate parents and children
    const parentFields = fields.filter((f) => f.parent_field_id === null);
    const childFieldsByParentId = fields.reduce((acc, f) => {
      if (f.parent_field_id) {
        if (!acc[f.parent_field_id]) acc[f.parent_field_id] = [];
        acc[f.parent_field_id].push(f);
      }
      return acc;
    }, {});

    // Filter parent fields by search
    const searchFilteredParents = lowerSearch
      ? parentFields.filter(
          (field) =>
            field.field_label?.toLowerCase().includes(lowerSearch) ||
            field.field_name?.toLowerCase().includes(lowerSearch),
        )
      : parentFields;

    return blocks.reduce((acc, block) => {
      const result = [];

      for (const field of searchFilteredParents) {
        const children = childFieldsByParentId[field.field_id] ?? [];

        if (children.length > 0) {
          // For group fields: include if parent or any child matches the block
          const matchingChildren = children.filter(
            (child) => child.block_id === block.block_id,
          );
          const parentMatchesBlock = field.block_id === block.block_id;

          if (parentMatchesBlock || matchingChildren.length > 0) {
            result.push({
              ...field,
              children: matchingChildren,
            });
          }
        } else {
          // For simple fields: include if block_id matches directly
          if (field.block_id === block.block_id) {
            result.push(field);
          }
        }
      }

      acc[block.block_id] = result;
      return acc;
    }, {});
  }, [fields, blocks, searchField]);

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          height: "calc(100% - 50px)",
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.25,
            borderBottom: "1px solid #F3F4F6",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: 16, fontWeight: 600, color: "#111827" }}
            >
              Form Section Browser
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            px: 1.5,
            py: 1.25,
          }}
        >
          <Button
            variant="contained"
            size="small"
            disabled={loadingDoc}
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            onClick={() => {
              setOpenNewBlockDialog(true);
            }}
            color="primary"
          >
            New Section
          </Button>
        </Box>
        <TextField
          id="searchInput"
          value={searchBlock}
          onChange={(e) => handleChange(e, setSearchBlock)}
          size="small"
          placeholder="Search sections..."
          sx={{ px: 1.5, pb: 1.25, "& .MuiInputBase-input": { fontSize: 14 } }}
          InputProps={{
            sx: { padding: "0px 10px" },
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          variant="outlined"
        />
        {showFields && (
          <TextField
            id="searchInput"
            value={searchField}
            onChange={(e) => handleChange(e, setSearchField, true)}
            size="small"
            placeholder="Search assigned fields..."
            sx={{
              px: 1.5,
              pb: 1.25,
              "& .MuiInputBase-input": { fontSize: 14 },
            }}
            InputProps={{
              sx: { padding: "0px 10px" },
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            variant="outlined"
          />
        )}
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            px: 1.5,
            pb: 1.25,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showFields}
                onChange={handleShowFields}
                slotProps={{ input: { "aria-label": "controlled" } }}
              />
            }
            slotProps={{
              typography: {
                sx: { fontSize: "0.875rem" },
              },
            }}
            label="Group by Section"
          />
        </Box>
        {error && (
          <Alert severity={"error"} sx={{ borderRadius: 2, mx: 1.5, mb: 1.25 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box
            sx={{ px: 1.5, display: "flex", justifyContent: "center", mt: 2.5 }}
          >
            <CircularProgress color="warning" size="3rem" />
          </Box>
        ) : blocks.length === 0 ? (
          <Box sx={{ px: 1.5 }}>
            <Typography
              color="primary"
              variant="h6"
              mb={2}
              mt={2.5}
              sx={{ fontSize: "1rem" }}
              align="center"
            >
              You have no sections to display just yet
            </Typography>
          </Box>
        ) : (
          <>
            <Box flex={1} sx={{ px: 1.5, overflow: "auto" }}>
              <Stack gap={0.75}>
                {showFields
                  ? filteredBlock
                      ?.filter(
                        (block) =>
                          !searchField ||
                          !!fieldsByBlock[block.block_id]?.length,
                      )
                      ?.map((block, index) => {
                        const fields = fieldsByBlock?.[block?.block_id] ?? [];
                        return (
                          <Accordion
                            key={block?.id}
                            disableGutters
                            elevation={0}
                            sx={{
                              border: "1px solid #e0e0e0",
                              borderRadius: "12px !important",
                              overflow: "hidden",
                              borderLeft: "3px solid #1976d2",
                              "&:before": { display: "none" },
                              "&.Mui-expanded": {
                                boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                              },
                            }}
                          >
                            <AccordionSummary
                              expandIcon={
                                <ExpandMoreIcon sx={{ fontSize: "1.5rem" }} />
                              }
                              sx={{
                                gap: 2,
                              }}
                              slotProps={{
                                content: {
                                  sx: {
                                    display: "flex",
                                    flexDirection: "column",
                                  },
                                },
                              }}
                            >
                              <Box>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: "0.85rem",
                                    color: "rgba(0, 0, 0, 0.8)",
                                    fontWeight: 500,
                                  }}
                                >
                                  {block?.block_label}
                                </Typography>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    fontSize: "0.85rem",
                                    whiteSpace: "pre-wrap",
                                    wordBreak: "break-all",
                                  }}
                                >
                                  {block?.description}
                                </Typography>
                              </Box>
                            </AccordionSummary>

                            <AccordionDetails>
                              <Box
                                sx={{
                                  display: "flex",
                                  gap: 0.75,
                                  flexWrap: "wrap",
                                  alignItems: "center",
                                }}
                              >
                                <Box
                                  display={"flex"}
                                  alignItems={"center"}
                                  gap={1}
                                >
                                  <IconButton
                                    size="small"
                                    disabled={loadingDoc}
                                    onClick={() => handleAttach(block)}
                                    sx={{
                                      border: "1px solid #d0d7de",
                                      borderRadius: 1.5,
                                      backgroundColor: "transparent",
                                      p: "4.41px !important",
                                    }}
                                  >
                                    <AttachFileIcon
                                      sx={{
                                        fontSize: "0.95rem",
                                        transform: "rotate(45deg)",
                                      }}
                                    />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    disabled={loadingDoc}
                                    onClick={() => {
                                      setOpenNewBlockDialog(true);
                                      setSelectedBlock(block);
                                    }}
                                    sx={{
                                      border: "1px solid #d0d7de",
                                      borderRadius: 1.5,
                                      backgroundColor: "transparent",
                                      p: "4px !important",
                                    }}
                                  >
                                    <EditIcon sx={{ fontSize: "1rem" }} />
                                  </IconButton>
                                  <IconButton
                                    size="small"
                                    disabled={loadingDoc}
                                    onClick={() => {
                                      setOpenDeleteBlockDialog(true);
                                      setSelectedBlock(block);
                                    }}
                                    sx={{
                                      border: "1px solid",
                                      borderColor: "error.light",
                                      borderRadius: 1.5,
                                      backgroundColor: "transparent",
                                      p: "4px !important",
                                    }}
                                  >
                                    <DeleteIcon
                                      sx={{ fontSize: "1rem" }}
                                      color="error"
                                    />
                                  </IconButton>
                                </Box>
                              </Box>

                              <Box
                                sx={{
                                  mt: 1.25,
                                  pt: 1.25,
                                  borderTop: "1px dashed #e0e0e0",
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontSize: "0.7rem",
                                    color: "rgba(0, 0, 0, 0.45)",
                                    fontWeight: 600,
                                    textTransform: "uppercase",
                                    letterSpacing: "0.04em",
                                    mb: 0.75,
                                    display: "block",
                                  }}
                                >
                                  Form Fields
                                </Typography>
                                {fields?.length > 0 ? (
                                  <Box
                                    sx={{
                                      display: "flex",
                                      flexDirection: "column",
                                      gap: 0.5,
                                    }}
                                  >
                                    {fields?.map((field, index) => (
                                      <Accordion
                                        key={field?.field_id ?? index}
                                        disableGutters
                                        elevation={0}
                                        sx={{
                                          border: "1px solid #e0e0e0",
                                          borderRadius: "8px !important",
                                          overflow: "hidden",
                                          "&:before": { display: "none" },
                                          "&.Mui-expanded": {
                                            boxShadow:
                                              "0 2px 8px rgba(0,0,0,0.08)",
                                          },
                                        }}
                                      >
                                        <AccordionSummary
                                          expandIcon={
                                            <ExpandMoreIcon
                                              sx={{ fontSize: "1.2rem" }}
                                            />
                                          }
                                          sx={{
                                            gap: 1.5,
                                            minHeight: "36px !important",
                                          }}
                                          slotProps={{
                                            content: {
                                              sx: {
                                                display: "flex",
                                                flexDirection: "column",
                                                my: "8px !important",
                                              },
                                            },
                                          }}
                                        >
                                          <Box
                                            display="flex"
                                            justifyContent={"space-between"}
                                            alignItems="center"
                                            gap={0.5}
                                          >
                                            <Typography
                                              variant="body2"
                                              sx={{
                                                fontSize: "0.78rem",
                                                color: "rgba(0, 0, 0, 0.8)",
                                                fontWeight: 500,
                                                lineHeight: 1.4,
                                              }}
                                            >
                                              {field?.field_label}
                                            </Typography>
                                          </Box>
                                        </AccordionSummary>

                                        <AccordionDetails
                                          sx={{ pt: 0.5, pb: 1, px: 1.5 }}
                                        >
                                          <Typography
                                            variant="body1"
                                            sx={{
                                              fontSize: "0.85rem",
                                              whiteSpace: "pre-wrap",
                                              wordBreak: "break-all",
                                            }}
                                          >
                                            {field?.field_name}
                                          </Typography>
                                          <Box
                                            display={"flex"}
                                            alignItems={"center"}
                                            gap={1}
                                            mt={"6px"}
                                          >
                                            {field?.field_type && (
                                              <Chip
                                                label={startCase(
                                                  field.field_type,
                                                )}
                                                size="small"
                                                variant="filled"
                                                sx={{
                                                  fontSize: "0.7rem",
                                                  height: 20,
                                                  borderRadius: 1,
                                                }}
                                              />
                                            )}
                                            {field?.location && (
                                              <Chip
                                                label={startCase(
                                                  field.location,
                                                )}
                                                size="small"
                                                variant="filled"
                                                color="primary"
                                                sx={{
                                                  fontSize: "0.7rem",
                                                  height: 20,
                                                  borderRadius: 1,
                                                }}
                                              />
                                            )}
                                          </Box>
                                          {[
                                            "checkboxGroup",
                                            "selectGroup",
                                          ].includes(field?.field_type) &&
                                            field?.children?.length > 0 && (
                                              <Box
                                                sx={{
                                                  mt: 1.25,
                                                  pt: 1.25,
                                                  borderTop:
                                                    "1px dashed #e0e0e0",
                                                }}
                                              >
                                                <Typography
                                                  variant="caption"
                                                  sx={{
                                                    fontSize: "0.7rem",
                                                    color:
                                                      "rgba(0, 0, 0, 0.45)",
                                                    fontWeight: 600,
                                                    textTransform: "uppercase",
                                                    letterSpacing: "0.04em",
                                                    mb: 0.75,
                                                    display: "block",
                                                  }}
                                                >
                                                  Options
                                                </Typography>

                                                <Box
                                                  sx={{
                                                    display: "flex",
                                                    flexDirection: "column",
                                                    gap: 0.5,
                                                  }}
                                                >
                                                  {field?.children.map(
                                                    (option, index) => (
                                                      <Accordion
                                                        key={
                                                          option?.field_id ??
                                                          index
                                                        }
                                                        disableGutters
                                                        elevation={0}
                                                        sx={{
                                                          border:
                                                            "1px solid #e0e0e0",
                                                          borderRadius:
                                                            "8px !important",
                                                          overflow: "hidden",
                                                          "&:before": {
                                                            display: "none",
                                                          },
                                                          "&.Mui-expanded": {
                                                            boxShadow:
                                                              "0 2px 8px rgba(0,0,0,0.08)",
                                                          },
                                                        }}
                                                      >
                                                        <AccordionSummary
                                                          expandIcon={
                                                            <ExpandMoreIcon
                                                              sx={{
                                                                fontSize:
                                                                  "1.2rem",
                                                              }}
                                                            />
                                                          }
                                                          sx={{
                                                            gap: 1.5,
                                                            minHeight:
                                                              "36px !important",
                                                          }}
                                                          slotProps={{
                                                            content: {
                                                              sx: {
                                                                display: "flex",
                                                                flexDirection:
                                                                  "column",
                                                                my: "8px !important",
                                                              },
                                                            },
                                                          }}
                                                        >
                                                          <Box
                                                            display="flex"
                                                            justifyContent={
                                                              "space-between"
                                                            }
                                                            alignItems="center"
                                                            gap={0.5}
                                                          >
                                                            <Typography
                                                              variant="body2"
                                                              sx={{
                                                                fontSize:
                                                                  "0.78rem",
                                                                color:
                                                                  "rgba(0, 0, 0, 0.8)",
                                                                fontWeight: 500,
                                                                lineHeight: 1.4,
                                                              }}
                                                            >
                                                              {
                                                                option?.field_label
                                                              }
                                                            </Typography>
                                                          </Box>
                                                        </AccordionSummary>

                                                        <AccordionDetails
                                                          sx={{
                                                            pt: 0.5,
                                                            pb: 1,
                                                            px: 1.5,
                                                          }}
                                                        >
                                                          <Typography
                                                            variant="body1"
                                                            sx={{
                                                              fontSize:
                                                                "0.85rem",
                                                              whiteSpace:
                                                                "pre-wrap",
                                                              wordBreak:
                                                                "break-all",
                                                            }}
                                                          >
                                                            {option?.field_name}
                                                          </Typography>
                                                          {option?.location && (
                                                            <Chip
                                                              label={startCase(
                                                                option.location,
                                                              )}
                                                              size="small"
                                                              variant="filled"
                                                              color="primary"
                                                              sx={{
                                                                fontSize:
                                                                  "0.7rem",
                                                                height: 20,
                                                                borderRadius: 1,
                                                              }}
                                                            />
                                                          )}
                                                        </AccordionDetails>
                                                      </Accordion>
                                                    ),
                                                  )}
                                                </Box>
                                              </Box>
                                            )}
                                        </AccordionDetails>
                                      </Accordion>
                                    ))}
                                  </Box>
                                ) : (
                                  <Box sx={{ px: 1.5 }}>
                                    <Typography
                                      color="primary"
                                      variant="h6"
                                      mb={2}
                                      mt={2.5}
                                      sx={{ fontSize: "13px" }}
                                      align="center"
                                    >
                                      No fields assigned to this section yet.
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            </AccordionDetails>
                          </Accordion>
                        );
                      })
                  : blocks?.map((block, index) => (
                      <Card
                        sx={{
                          p: 1.5,
                          boxShadow: "none !important",
                          "--Paper-shadow": "none !important",
                          borderRadius: "12px !important",
                          border: "1px solid #e0e0e0",
                          borderLeft: "3px solid #1976d2",
                        }}
                        key={block?.id}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "start",
                            }}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                fontSize: "0.85rem",
                                color: "rgba(0, 0, 0, 0.8)",
                                fontWeight: 500,
                              }}
                            >
                              {block?.block_label}
                            </Typography>
                            <Box display={"flex"} alignItems={"center"} gap={1}>
                              <IconButton
                                size="small"
                                disabled={loadingDoc}
                                onClick={() => handleAttach(block)}
                                sx={{
                                  border: "1px solid #d0d7de",
                                  borderRadius: 1.5,
                                  backgroundColor: "transparent",
                                  p: "4.41px !important",
                                }}
                              >
                                <AttachFileIcon
                                  sx={{
                                    fontSize: "0.95rem",
                                    transform: "rotate(45deg)",
                                  }}
                                />
                              </IconButton>
                              <IconButton
                                size="small"
                                disabled={loadingDoc}
                                onClick={() => {
                                  setOpenNewBlockDialog(true);
                                  setSelectedBlock(block);
                                }}
                                sx={{
                                  border: "1px solid #d0d7de",
                                  borderRadius: 1.5,
                                  backgroundColor: "transparent",
                                  p: "4px !important",
                                }}
                              >
                                <EditIcon sx={{ fontSize: "1rem" }} />
                              </IconButton>
                              <IconButton
                                size="small"
                                disabled={loadingDoc}
                                onClick={() => {
                                  setOpenDeleteBlockDialog(true);
                                  setSelectedBlock(block);
                                }}
                                sx={{
                                  border: "1px solid",
                                  borderColor: "error.light",
                                  borderRadius: 1.5,
                                  backgroundColor: "transparent",
                                  p: "4px !important",
                                }}
                              >
                                <DeleteIcon
                                  sx={{ fontSize: "1rem" }}
                                  color="error"
                                />
                              </IconButton>
                            </Box>
                          </Box>
                          <Typography
                            variant="body1"
                            sx={{
                              fontSize: "0.85rem",
                              whiteSpace: "pre-wrap",
                              wordBreak: "break-all",
                            }}
                          >
                            {block?.description}
                          </Typography>
                        </Box>
                      </Card>
                    ))}
              </Stack>
            </Box>
            <Box sx={{ py: 1.5, display: "flex", justifyContent: "center" }}>
              <Pagination
                count={totalPage}
                page={page}
                onChange={handlePageChange}
                siblingCount={0}
                size={"small"}
              />
            </Box>
          </>
        )}
      </Box>
      {openNewBlockDialog && (
        <NewBlockDialog
          open={openNewBlockDialog}
          handleClose={handleCloseNewBlockDialog}
          getBlocks={getBlocks}
          searchBlock={searchBlock}
          selectedBlock={selectedBlock}
          setPage={setPage}
        />
      )}
      <Dialog
        open={openDeleteBlockDialog}
        onClose={handleCloseDeleteBlockDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            Are you sure you want to remove{" "}
            <strong>{selectedBlock?.block_label}</strong> section?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDeleteBlockDialog}
            disabled={loading}
            variant="outlined"
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleDeleteConfirm}
            loading={loading}
            variant="contained"
            color="error"
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default FormBlockBrowser;
